import React, { useState, useEffect } from 'react';
import { 
  Box, Paper, Typography, Grid, Table, TableBody, TableCell, TableHead, TableRow, 
  TableContainer, Button, IconButton, Chip, Dialog, DialogTitle, DialogContent, 
  DialogActions, TextField, FormControlLabel, Switch, CircularProgress, Alert, Snackbar 
} from '@mui/material';
import { Edit, Add } from '@mui/icons-material';
import api from '../../api';

const MeetingConfiguration = () => {
  const [purposes, setPurposes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Modal State
  const [modal, setModal] = useState({ open: false, type: '', isEdit: false, id: null });
  const [formData, setFormData] = useState({ name: '', is_active: true });
  const [submitting, setSubmitting] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [purpRes, locRes] = await Promise.all([
        api.get('/meeting-masters/purposes'),
        api.get('/meeting-masters/locations')
      ]);
      setPurposes(purpRes.data);
      setLocations(locRes.data);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to fetch master data. Ensure you have Admin rights.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const openModal = (type, item = null) => {
    if (item) {
      setFormData({ name: type === 'PURPOSE' ? item.purpose_name : item.location_name, is_active: item.is_active });
      setModal({ open: true, type, isEdit: true, id: item.id });
    } else {
      setFormData({ name: '', is_active: true });
      setModal({ open: true, type, isEdit: false, id: null });
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) return setMessage({ type: 'error', text: 'Name is mandatory.' });
    setSubmitting(true);
    
    try {
      const endpoint = modal.type === 'PURPOSE' ? '/meeting-masters/purposes' : '/meeting-masters/locations';
      const url = modal.isEdit ? `${endpoint}/${modal.id}` : endpoint;
      const method = modal.isEdit ? 'put' : 'post';
      
      await api[method](url, formData);
      setMessage({ type: 'success', text: `${modal.type === 'PURPOSE' ? 'Purpose' : 'Location'} saved successfully.` });
      setModal({ open: false, type: '', isEdit: false, id: null });
      fetchData();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to save record.' });
    } finally {
      setSubmitting(false);
    }
  };

  const MasterTable = ({ title, data, type, nameField }) => (
    <Paper elevation={3} sx={{ p: 3, borderRadius: 2, height: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
        <Typography variant="h6" color="primary">{title}</Typography>
        <Button variant="contained" size="small" startIcon={<Add />} onClick={() => openModal(type)}>
          Add New
        </Button>
      </Box>
      <TableContainer sx={{ maxHeight: 400 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Edit</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>{row.id}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{row[nameField]}</TableCell>
                <TableCell>
                  <Chip size="small" label={row.is_active ? 'Active' : 'Inactive'} color={row.is_active ? 'success' : 'default'} />
                </TableCell>
                <TableCell align="center">
                  <IconButton size="small" color="primary" onClick={() => openModal(type, row)}>
                    <Edit fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {data.length === 0 && (
              <TableRow><TableCell colSpan={4} align="center">No records found.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" color="primary" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
        Meeting Configurations (Master Data)
      </Typography>

      {message.text && <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage({type:'', text:''})}>{message.text}</Alert>}

      {loading ? <CircularProgress sx={{ display: 'block', m: 'auto' }} /> : (
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <MasterTable title="Meeting Purposes" data={purposes} type="PURPOSE" nameField="purpose_name" />
          </Grid>
          <Grid item xs={12} md={6}>
            <MasterTable title="Meeting Locations" data={locations} type="LOCATION" nameField="location_name" />
          </Grid>
        </Grid>
      )}

      {/* CREATE / EDIT MODAL */}
      <Dialog open={modal.open} onClose={() => setModal({ ...modal, open: false })} fullWidth maxWidth="sm">
        <DialogTitle sx={{ bgcolor: 'primary.main', color: '#fff' }}>
          {modal.isEdit ? 'Edit' : 'Add New'} {modal.type === 'PURPOSE' ? 'Meeting Purpose' : 'Meeting Location'}
        </DialogTitle>
        <DialogContent dividers>
          <TextField 
            fullWidth required sx={{ mt: 1, mb: 3 }}
            label="Name" 
            value={formData.name} 
            onChange={(e) => setFormData({ ...formData, name: e.target.value })} 
          />
          <FormControlLabel
            control={<Switch checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} />}
            label={formData.is_active ? 'Status: Active (Visible in Dropdowns)' : 'Status: Inactive (Hidden)'}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setModal({ ...modal, open: false })} disabled={submitting}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={submitting}>
            {submitting ? 'Saving...' : 'Save Configuration'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MeetingConfiguration;